import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack5';

// material
import {
  Card,
  Stack,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  FormHelperText,
  FormControlLabel,
  Switch,
  Autocomplete
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import QrReader from 'react-qr-reader';

// components
import Page from '../components/Page';
import LoadingScreen from '../components/LoadingScreen';

// API service
import documentsApi from '../utils/api/requiredDocuments';
import userAPI from '../utils/api/users';
import { logSuccessAction, ACTION_CATEGORIES } from '../utils/actionLogger';
import storage from '../utils/storage';
import Bugsnag from '@bugsnag/js';

const moment = require('moment-timezone');
moment().tz('Asia/Manila').format();

// Constants for form validation
const FILE_TYPES = [
  'pdf',
  'doc',
  'docx',
  'jpg',
  'jpeg',
  'png'
];

const StoreRequiredDocuments = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [user, setUser] = useState(null);
  
  // Group store states
  const [company, setCompany] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [openQR, setOpenQR] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    allowedFileTypes: [],
    maxFileSize: 5, // Default 5MB
    numberOfFiles: 1, // Default 1 file
    requiresExpiration: false // Default false
  });
  
  // Validation errors
  const [errors, setErrors] = useState({
    name: '',
    allowedFileTypes: '',
    maxFileSize: '',
    numberOfFiles: ''
  });

  // Load documents and user on component mount
  useEffect(() => {
    const loadUserAndDocuments = async () => {
      // Load user data
      const local_user = await storage.getUser();
      if (local_user) {
        const parsedUser = JSON.parse(local_user);
        setUser(parsedUser);
        
        // Load group stores for admin users (role >= 3)
        if (parsedUser.role >= 3) {
          try {
            const getCompany = await userAPI.get_group_store(parsedUser._id);
            if (getCompany.status === 200 && getCompany.data.report.store) {
              const stores = getCompany.data.report.store;
              setCompany(stores);
              if (stores.length > 0) {
                const firstStore = stores[0];
                setSelectedCompany(firstStore);
                // Load documents for first store
                await loadDocuments(firstStore);
              } else {
                setSelectedCompany('');
                await loadDocuments();
              }
            } else {
              // Fallback to user's own company
              setSelectedCompany(parsedUser.company || '');
              await loadDocuments();
            }
          } catch (error) {
            console.error('Error fetching group stores:', error);
            Bugsnag.notify(error);
            setSelectedCompany(parsedUser.company || '');
            await loadDocuments();
          }
        } else {
          // Non-admin users use their own company
          setSelectedCompany(parsedUser.company || '');
          await loadDocuments();
        }
      }
    };
    
    loadUserAndDocuments();
  }, []);

  // Load documents using the API service
  const loadDocuments = async (companyOverride = null) => {
    setLoading(true);
    try {
      const targetCompany = companyOverride || selectedCompany || (user && user.company);
      const response = await documentsApi.getAllRequiredDocuments(targetCompany);
      if (response.ok) {
        // Transform documents to work with DataGrid
        const formattedDocs = response.data.map(doc => ({
          ...doc,
          id: doc._id || doc.id // Support both MongoDB _id and client-side id
        }));
        setDocuments(formattedDocs);
      } else {
        enqueueSnackbar(response.error || 'Error loading documents', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Error loading documents', { variant: 'error' });
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate form data
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      name: '',
      allowedFileTypes: '',
      maxFileSize: '',
      numberOfFiles: ''
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Document name is required';
      valid = false;
    }

    if (formData.allowedFileTypes.length === 0) {
      newErrors.allowedFileTypes = 'At least one file type must be selected';
      valid = false;
    }

    if (!formData.maxFileSize || formData.maxFileSize <= 0) {
      newErrors.maxFileSize = 'File size must be greater than 0MB';
      valid = false;
    }

    if (!formData.numberOfFiles || formData.numberOfFiles <= 0) {
      newErrors.numberOfFiles = 'Number of files must be greater than 0';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // Handle opening the add document dialog
  const handleAddDocument = () => {
    setFormData({
      name: '',
      allowedFileTypes: [],
      maxFileSize: 5,
      numberOfFiles: 1,
      requiresExpiration: false
    });
    setErrors({
      name: '',
      allowedFileTypes: '',
      maxFileSize: '',
      numberOfFiles: ''
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  // Handle opening the edit document dialog
  const handleEditDocument = async (id) => {
    setLoading(true);
    try {
      const response = await documentsApi.getRequiredDocumentById(id);
      if (response.ok && response.data) {
        setFormData({
          name: response.data.name,
          allowedFileTypes: response.data.allowedFileTypes,
          maxFileSize: response.data.maxFileSize,
          numberOfFiles: response.data.numberOfFiles,
          requiresExpiration: response.data.requiresExpiration || false
        });
        setSelectedDocId(id);
        setEditMode(true);
        setOpenDialog(true);
      } else {
        enqueueSnackbar(response.error || 'Error fetching document', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Error fetching document', { variant: 'error' });
      console.error('Error fetching document:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a document
  const handleDeleteDocument = async (id) => {
    if (window.confirm('Are you sure you want to delete this document requirement?')) {
      setLoading(true);
      
      // Find the document being deleted for logging
      const documentToDelete = documents.find(doc => doc.id === id);
      
      try {
        const response = await documentsApi.deleteRequiredDocument(id);
        if (response.ok) {
          logSuccessAction({
            user: user._id,
            company: user.company,
            userDisplay: {
              name: user.displayName,
              email: user.email
            },
            role: user.role,
            action: 'Delete Required Document',
            category: ACTION_CATEGORIES.REQUIRED_DOCUMENTS,
            details: `Successfully deleted required document "${documentToDelete.name}" for ${user.company}. File types: ${documentToDelete.allowedFileTypes.join(', ')}. Max size: ${documentToDelete.maxFileSize}MB. Files required: ${documentToDelete.numberOfFiles}. Requires expiration: ${documentToDelete.requiresExpiration ? 'Yes' : 'No'}`
          });
          
          enqueueSnackbar('Document deleted successfully', { variant: 'success' });
          loadDocuments(selectedCompany); // Reload documents after deletion
        } else {
          enqueueSnackbar(response.error || 'Error deleting document', { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar('Error deleting document', { variant: 'error' });
        console.error('Error deleting document:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle switch input changes
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Handle file type selection
  const handleFileTypeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      allowedFileTypes: e.target.value
    }));
  };

  // Handle store selection change
  const handleStoreChange = async (event, value) => {
    if (value === selectedCompany || value === null) return;

    setLoading(true);
    try {
      setSelectedCompany(value);
      await loadDocuments(value);
    } catch (error) {
      console.error('Error changing store:', error);
      enqueueSnackbar('Error loading documents for selected store', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle store deletion
  const handleDeleteStore = async () => {
    if (window.confirm('Are you sure you want to remove this store from your group?')) {
      setLoading(true);
      try {
        const data = {
          store: selectedCompany,
          id: user._id
        };
        await userAPI.delete_group_store(data);
        
        // Reload stores after deletion
        const getCompany = await userAPI.get_group_store(user._id);
        if (getCompany.status === 200 && getCompany.data.report.store) {
          const stores = getCompany.data.report.store;
          setCompany(stores);
          if (stores.length > 0) {
            const firstStore = stores[0];
            setSelectedCompany(firstStore);
            await loadDocuments(firstStore);
          } else {
            setCompany([]);
            setSelectedCompany('');
            setDocuments([]);
          }
        }
        
        enqueueSnackbar('Store removed successfully', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting store:', error);
        enqueueSnackbar('Error removing store', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle opening QR modal
  const handleOpenQRModal = () => {
    setOpenQR(true);
  };

  // Handle closing QR modal
  const handleCloseQR = () => {
    setOpenQR(false);
  };

  // Handle QR scan error
  const handleQRError = (err) => {
    console.error('QR Scan Error:', err);
    enqueueSnackbar('QR scan error occurred', { variant: 'error' });
  };

  // Handle QR scan success
  const handleQRScan = async (result) => {
    if (result) {
      try {
        const url = new URL(result);
        const pathSegments = url.pathname.split('/');
        const storeId = pathSegments[1];
        
        const data = {
          uid: user._id,
          storeid: storeId
        };
        
        const response = await userAPI.post_group_store(data);
        if (response.status === 200) {
          setOpenQR(false);
          enqueueSnackbar('Store added successfully', { variant: 'success' });
          
          // Reload stores after addition
          const getCompany = await userAPI.get_group_store(user._id);
          if (getCompany.status === 200) {
            setCompany(getCompany.data.report.store);
          }
        } else {
          enqueueSnackbar('Failed to add store', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error processing QR code:', error);
        enqueueSnackbar('Invalid QR code', { variant: 'error' });
      }
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    
    // Get original document for edit logging
    const originalDocument = editMode ? documents.find(doc => doc.id === selectedDocId) : null;
    
    try {
      let response;
      
      const documentData = {
        ...formData,
        company: selectedCompany || (user && user.company)
      };
      
      if (editMode) {
        response = await documentsApi.updateRequiredDocument(selectedDocId, documentData);
      } else {
        response = await documentsApi.createRequiredDocument(documentData);
      }
      
      if (response.ok) {
        // Log successful document creation/update
        if (user) {
          if (editMode && originalDocument) {
            // Log edit with changes
            const changes = [];
            if (formData.name !== originalDocument.name) {
              changes.push(`Name: "${originalDocument.name}" → "${formData.name}"`);
            }
            if (JSON.stringify(formData.allowedFileTypes) !== JSON.stringify(originalDocument.allowedFileTypes)) {
              changes.push(`File types: "${originalDocument.allowedFileTypes.join(', ')}" → "${formData.allowedFileTypes.join(', ')}"`);
            }
            if (formData.maxFileSize !== originalDocument.maxFileSize) {
              changes.push(`Max size: "${originalDocument.maxFileSize}MB" → "${formData.maxFileSize}MB"`);
            }
            if (formData.numberOfFiles !== originalDocument.numberOfFiles) {
              changes.push(`Files required: "${originalDocument.numberOfFiles}" → "${formData.numberOfFiles}"`);
            }
            if (formData.requiresExpiration !== originalDocument.requiresExpiration) {
              changes.push(`Requires expiration: "${originalDocument.requiresExpiration ? 'Yes' : 'No'}" → "${formData.requiresExpiration ? 'Yes' : 'No'}"`);
            }
            
            logSuccessAction({
              user: user._id,
              company: user.company,
              userDisplay: {
                name: user.displayName,
                email: user.email
              },
              role: user.role,
              action: 'Edit Required Document',
              category: ACTION_CATEGORIES.REQUIRED_DOCUMENTS,
              details: `Successfully edited required document "${originalDocument.name}" for ${user.company}${changes.length > 0 ? `. Changes: ${changes.join(', ')}` : ''}`
            });
          } else {
            // Log creation
            logSuccessAction({
              user: user._id,
              company: user.company,
              userDisplay: {
                name: user.displayName,
                email: user.email
              },
              role: user.role,
              action: 'Create Required Document',
              category: ACTION_CATEGORIES.USER_MANAGEMENT,
              details: `Successfully created new required document "${formData.name}" for ${user.company}. File types: ${formData.allowedFileTypes.join(', ')}. Max size: ${formData.maxFileSize}MB. Files required: ${formData.numberOfFiles}. Requires expiration: ${formData.requiresExpiration ? 'Yes' : 'No'}`
            });
          }
        }
        
        enqueueSnackbar(`Document ${editMode ? 'updated' : 'created'} successfully`, { variant: 'success' });
        setOpenDialog(false);
        loadDocuments(selectedCompany); // Reload documents after creation/update
      } else {
        enqueueSnackbar(response.error || `Error ${editMode ? 'updating' : 'creating'} document`, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(`Error ${editMode ? 'updating' : 'creating'} document`, { variant: 'error' });
      console.error(`Error ${editMode ? 'updating' : 'creating'} document:`, error);
    } finally {
      setLoading(false);
    }
  };

  // DataGrid columns configuration
  const columns = [
    { field: 'name', headerName: 'Document Name', flex: 1 },
    { 
      field: 'allowedFileTypes', 
      headerName: 'Allowed File Types', 
      flex: 1,
      renderCell: (params) => params.value.join(', ')
    },
    { 
      field: 'maxFileSize', 
      headerName: 'Max Size (MB)', 
      width: 150,
      renderCell: (params) => `${params.value} MB`
    },
    { 
      field: 'numberOfFiles', 
      headerName: 'Files Required', 
      width: 150 
    },
    {
      field: 'requiresExpiration',
      headerName: 'Requires Expiration',
      width: 170,
      renderCell: (params) => (
        params.value ? 'Yes' : 'No'
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton 
            size="small" 
            onClick={() => handleEditDocument(params.row.id)}
            disabled={isLoading}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDeleteDocument(params.row.id)}
            disabled={isLoading}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      )
    }
  ];

  return (
    <Page title="Required Documents | Time In">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Required Documents
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddDocument}
            disabled={isLoading}
          >
            Add Document
          </Button>
        </Stack>
        
        {/* Group Store Selection for Admin Users */}
        {user && user.role >= 3 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <Box display="flex" alignItems="center">
                  <Autocomplete
                    id="store-select"
                    value={selectedCompany || ''}
                    onChange={handleStoreChange}
                    options={company}
                    placeholder="Store"
                    renderInput={(params) => (
                      <TextField {...params} label="Store" />
                    )}
                    sx={{
                      flexGrow: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'auto',
                      textOverflow: 'ellipsis',
                      pt: 1
                    }}
                  />
                  <IconButton 
                    color="primary" 
                    aria-label="add store" 
                    onClick={handleOpenQRModal} 
                    sx={{ ml: 1 }}
                    disabled={isLoading}
                  >
                    <AddIcon />
                  </IconButton>
                  <IconButton 
                    color="primary" 
                    aria-label="delete store" 
                    onClick={handleDeleteStore} 
                    sx={{ ml: 0 }}
                    disabled={isLoading || !selectedCompany}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        )}
        
        {isLoading && !documents.length ? (
          <Box sx={{ height: '50vh' }}>
            <LoadingScreen />
          </Box>
        ) : (
          <Card sx={{ my: 5 }}>
            <div style={{ height: '60vh', width: '100%' }}>
              <DataGrid
                rows={documents}
                columns={columns}
                pageSize={10}
                loading={isLoading}
                disableSelectionOnClick
                components={{
                  NoRowsOverlay: () => (
                    <Stack height="100%" alignItems="center" justifyContent="center">
                      <Typography variant="body1" color="textSecondary">
                        No documents found. Click "Add Document" to create one.
                      </Typography>
                    </Stack>
                  )
                }}
              />
            </div>
          </Card>
        )}

        {/* Add/Edit Document Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => !isLoading && setOpenDialog(false)} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                {editMode ? 'Edit Document' : 'Add Document'}
              </Typography>
              <IconButton onClick={() => !isLoading && setOpenDialog(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Document Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.allowedFileTypes} disabled={isLoading}>
                  <InputLabel>Allowed File Types</InputLabel>
                  <Select
                    multiple
                    name="allowedFileTypes"
                    value={formData.allowedFileTypes}
                    onChange={handleFileTypeChange}
                  >
                    {FILE_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.allowedFileTypes && (
                    <FormHelperText>{errors.allowedFileTypes}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Max File Size (MB)"
                  name="maxFileSize"
                  type="number"
                  value={formData.maxFileSize}
                  onChange={handleInputChange}
                  error={!!errors.maxFileSize}
                  helperText={errors.maxFileSize}
                  InputProps={{ inputProps: { min: 1 } }}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Number of Files Required"
                  name="numberOfFiles"
                  type="number"
                  value={formData.numberOfFiles}
                  onChange={handleInputChange}
                  error={!!errors.numberOfFiles}
                  helperText={errors.numberOfFiles}
                  InputProps={{ inputProps: { min: 1 } }}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.requiresExpiration}
                      onChange={handleSwitchChange}
                      name="requiresExpiration"
                      color="primary"
                      disabled={isLoading}
                    />
                  }
                  label="Document Requires Expiration Date"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  If enabled, employees will be required to provide an expiration date when uploading this document.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenDialog(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : editMode ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* QR Code Scanner Dialog */}
        <Dialog open={openQR} onClose={handleCloseQR}>
          <DialogTitle>
            Scan store QR code
            <IconButton
              aria-label="close"
              onClick={handleCloseQR}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <QrReader
              delay={3000}
              onError={handleQRError}
              onScan={handleQRScan}
              style={{ width: '250px', height: '200px' }}
            />
          </DialogContent>
        </Dialog>
      </Container>
    </Page>
  );
};

export default StoreRequiredDocuments;
